import { QueryHookOptions, useQuery } from '@apollo/client'
import gql from 'graphql-tag'
import {
  dealCalculationFragments,
  dealFragments,
  itemFragments,
} from '@/domains/items/hooks'
import { useShowErrorsPopup } from '@/hooks'
import { fragments } from '@/hooks/fragments'
import { Deal } from '@/schemaTypes'

const GET_AUCTION_BUYBACK_DEALS = gql`
  query getAuctionBuybackDeals($auctionId: ObjectId!, $auctionDate: DateTime!) {
    getAuctionBuybackDeals(auctionId: $auctionId) {
      _id
      dealStartDate
      createdAt
      updatedAt
      dealType
      bookingNumber
      shop {
        _id
        name
      }
      dealFinalValues {
        payoutAmount
        shouldOverwritePayoutAmount
        durationInDays
      }
      customer {
        ... on Customer {
          _id
          firstname
          lastname
          dateOfBirth
          email
          phone
        }
      }
      company {
        _id
        configuration {
          liquidationFee
        }
      }
      events {
        ...DealEventFragment
      }
      items {
        _id
        title
        storageLabel
        internalSaleData {
          price
          date
          purchasedDirectly
          isCashyPrice
          buyerData {
            partnerId
            name
          }
        }
        itemValorizationCalculation {
          ...ItemValorizationCalculationFragment
        }
        dealsHistory {
          values {
            verified {
              loanToValue
              payoutAmount
            }
          }
        }
        events {
          ...ItemEventFragment
        }
      }
      auctionDealCalculation(auctionDate: $auctionDate) {
        ...DealCalculationFragment
      }
      pawnData {
        buyBackData {
          repurchaseFee
          partialRepayment
          grossProfit
          isPawnAgain
          totalRepurchaseFee
        }
        paybackData {
          ...PaymentDataFragment
        }
      }
    }
  }

  ${itemFragments.itemEvent}
  ${fragments.fileMetadata}
  ${itemFragments.itemValorizationCalculation}
  ${fragments.paymentData}

  ${dealFragments.event}
  ${dealCalculationFragments.dealCalculation}
`

type TData = { getAuctionBuybackDeals: Deal[] }
type TVariables = { auctionId: string; auctionDate: Date }

export function useGetAuctionBuybackDeals(
  opts: QueryHookOptions<TData, TVariables> = {},
) {
  const queryResult = useQuery<TData, TVariables>(GET_AUCTION_BUYBACK_DEALS, {
    fetchPolicy: 'network-only',
    ...opts,
  })

  useShowErrorsPopup(queryResult.error)

  return {
    queryResult,
    deals: queryResult.data?.getAuctionBuybackDeals || [],
  }
}
